import { useEffect, useState } from 'react';
import { AlertDialog } from '@/components/ui/alert-dialog.js';
import type { Triple } from './share-row.js';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function triplesDiffer(a: Triple, b: Triple): boolean {
  return (
    round2(a.corePct) !== round2(b.corePct) ||
    round2(a.supportingPct) !== round2(b.supportingPct) ||
    round2(a.outPct) !== round2(b.outPct)
  );
}

interface Props {
  open: boolean;
  projectName: string;
  /** The triple the reviewer is about to submit (from the row's editable inputs). */
  pending: Triple | null;
  /** The project's current AI-derived triple, for the deviation check. Null when unavailable. */
  derived: Triple | null;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onConfirm: (note: string | null) => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog for adopting a project's R&D share (CLAUDE.md §6.1: destructive/binding
 * confirmations use <AlertDialog>, never a modal). The note field becomes required — with an
 * inline validation message, not a disabled button — whenever the adopted triple deviates from
 * the AI-derived one, mirroring MoveBucketModal's override-reason requirement. Also defensively
 * re-checks the sum-to-100 invariant here (the row already disables the Adopt button on an
 * invalid sum, but this dialog is the actual gate before the API call).
 */
export function AdoptShareDialog({
  open,
  projectName,
  pending,
  derived,
  isSubmitting,
  errorMessage,
  onConfirm,
  onCancel,
}: Props) {
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote('');
      setValidationError(null);
    }
  }, [open]);

  if (!pending) return null;

  const sum = pending.corePct + pending.supportingPct + pending.outPct;
  const sumValid = Math.abs(sum - 100) < 0.01;
  const deviates = !derived || triplesDiffer(pending, derived);

  function handleConfirm() {
    if (!pending) return;
    if (!sumValid) {
      setValidationError(`Core + Supporting + Out must sum to 100 (currently ${round2(sum)}).`);
      return;
    }
    if (deviates && !note.trim()) {
      setValidationError('A note is required because this differs from the AI-derived split.');
      return;
    }
    setValidationError(null);
    onConfirm(note.trim() ? note.trim() : null);
  }

  return (
    <AlertDialog
      open={open}
      title={`Adopt R&D share — ${projectName}`}
      confirmLabel={isSubmitting ? 'Adopting…' : 'Adopt'}
      busy={isSubmitting}
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <p className="text-muted-foreground">Core</p>
            <p className="font-mono">{round2(pending.corePct)}%</p>
          </div>
          <div>
            <p className="text-muted-foreground">Supporting</p>
            <p className="font-mono">{round2(pending.supportingPct)}%</p>
          </div>
          <div>
            <p className="text-muted-foreground">Out</p>
            <p className="font-mono">{round2(pending.outPct)}%</p>
          </div>
        </div>

        {derived && deviates && (
          <p className="text-xs text-muted-foreground">
            AI-derived split was {round2(derived.corePct)}/{round2(derived.supportingPct)}/{round2(derived.outPct)}.
          </p>
        )}

        <div className="space-y-1">
          <label htmlFor="adopt-share-note" className="text-sm font-medium">
            Note {deviates && <span className="text-destructive">*</span>}
          </label>
          <textarea
            id="adopt-share-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={deviates ? 'Required — explain why this differs from the AI-derived split' : 'Optional'}
            rows={3}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {validationError && <p className="text-xs text-destructive">{validationError}</p>}
        {errorMessage && <p className="text-xs text-destructive">{errorMessage}</p>}
      </div>
    </AlertDialog>
  );
}
